import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from './Icon'
import { cx } from '@/lib/cx'
import { formatAxisDate, formatPointDate } from '@/lib/dates'

export interface TrendPoint {
  day: string
  consultations: number
}

const HEIGHT = 208
/* A marker sitting exactly on the top gridline or the baseline is half outside
   the box and reads as clipped, so the value band is inset from both. */
const PAD = 8
const FALLBACK_WIDTH = 720
const MAX_X_TICKS = 6
/* Above this, one mark per day is a hairball: a year of 0-3 counts drawn daily
   is 365 spikes with no trend visible in any of them. */
const CONDENSE_ABOVE = 92

/**
 * Consultations over time. An area chart, not bars: on bars a day with zero
 * consultations renders as nothing, and a gap reads as "no data" rather than
 * "the clinic was closed".
 */
export function TrendChart({
  points,
  label,
  unit = 'consultations',
}: {
  points: TrendPoint[]
  label: string
  unit?: string
}) {
  const plotRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(FALLBACK_WIDTH)
  const [cursor, setCursor] = useState<number | null>(null)
  const [tableOpen, setTableOpen] = useState(false)

  // Real pixels rather than a scaled viewBox: `preserveAspectRatio="none"`
  // would squash the markers into ellipses and the strokes with them.
  useEffect(() => {
    const el = plotRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width ?? 0
      if (next > 0) setWidth(next)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // The caption always describes the real days; only the plot is condensed.
  const total = points.reduce((sum, p) => sum + p.consultations, 0)
  const days = points.length

  const { points: plotted, grouped } = useMemo(() => condense(points), [points])
  const scale = useMemo(
    () => niceScale(Math.max(0, ...plotted.map((p) => p.consultations))),
    [plotted],
  )

  if (days === 0) {
    return <p className="py-8 text-center text-sm text-fg-subtle">No {unit} recorded in this period.</p>
  }

  const count = plotted.length

  const band = width / count
  const x = (index: number) => band * (index + 0.5)
  const y = (value: number) => HEIGHT - PAD - (value / scale.top) * (HEIGHT - PAD * 2)

  const base = y(0)
  const line = plotted.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.consultations)}`).join(' ')
  const area = `${line} L${x(count - 1)},${base} L${x(0)},${base} Z`

  // As many date ticks as fit without collision — never one per point.
  const tickCount = Math.max(2, Math.min(count, MAX_X_TICKS, Math.floor(width / 84)))
  const xTicks =
    count === 1
      ? [0]
      : Array.from({ length: tickCount }, (_, i) =>
          Math.round((i * (count - 1)) / (tickCount - 1)),
        ).filter((v, i, all) => all.indexOf(v) === i)

  const active = cursor !== null ? plotted[cursor] : undefined
  const period = grouped ? 'week' : 'day'
  const summary = `${label}. ${total} in total across ${days} days, plotted by ${period}. ${plotted
    .map((p) => `${formatAxisDate(p.day)}: ${p.consultations}`)
    .join(', ')}`

  function move(delta: number) {
    setCursor((current) => {
      const next = (current ?? (delta > 0 ? -1 : count)) + delta
      return Math.max(0, Math.min(count - 1, next))
    })
  }

  return (
    <figure className="grid gap-3">
      <figcaption className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
        <span className="text-3xl leading-tight font-bold tracking-tight tabular-nums">{total}</span>
        <span className="text-sm text-fg-muted">
          {unit} over {days} days
        </span>
        <span className="text-sm text-fg-muted tabular-nums">{(total / days).toFixed(1)} a day on average</span>
        {grouped && <span className="text-sm text-fg-muted tabular-nums">Plotted weekly</span>}
      </figcaption>

      <div className="grid grid-cols-[auto_1fr] gap-2">
        <div className="relative w-[2ch]" style={{ height: HEIGHT }} aria-hidden="true">
          {scale.ticks.map((tick) => (
            <span key={tick} className="absolute right-0 -translate-y-1/2 text-xs leading-none whitespace-nowrap text-fg-subtle tabular-nums" style={{ top: y(tick) }}>
              {tick}
            </span>
          ))}
        </div>

        <div
          ref={plotRef}
          className="relative min-w-0 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus"
          role="img"
          aria-label={summary}
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') move(1)
            else if (event.key === 'ArrowLeft') move(-1)
            else if (event.key === 'Home') setCursor(0)
            else if (event.key === 'End') setCursor(count - 1)
            else if (event.key === 'Escape') setCursor(null)
            else return
            event.preventDefault()
          }}
          onBlur={() => setCursor(null)}
          onPointerMove={(event) => {
            const box = event.currentTarget.getBoundingClientRect()
            if (box.width === 0) return
            const index = Math.floor(((event.clientX - box.left) / box.width) * count)
            setCursor(Math.max(0, Math.min(count - 1, index)))
          }}
          onPointerLeave={() => setCursor(null)}
        >
          <svg
            className="block w-full touch-pan-y"
            width={width}
            height={HEIGHT}
            viewBox={`0 0 ${width} ${HEIGHT}`}
            aria-hidden="true"
            focusable="false"
          >
            {scale.ticks.map((tick) => (
              <line
                key={tick}
                className={tick === 0 ? 'stroke-chart-axis stroke-1 [shape-rendering:crispEdges]' : 'stroke-chart-grid stroke-1 [shape-rendering:crispEdges]'}
                x1={0}
                x2={width}
                y1={y(tick)}
                y2={y(tick)}
              />
            ))}

            <path className="fill-chart-fill" d={area} />
            <path className="fill-none stroke-chart-line stroke-2 [stroke-linecap:round] [stroke-linejoin:round]" d={line} />

            {/* Markers only while they stay markers — at 90 points they merge
                into a bead chain and the line reads better alone. */}
            {count <= 31 &&
              plotted.map((point, i) => (
                <circle
                  key={point.day}
                  className="fill-chart-line stroke-surface stroke-2"
                  cx={x(i)}
                  cy={y(point.consultations)}
                  r={4}
                />
              ))}

            {cursor !== null && active && (
              <>
                <line
                  className="stroke-chart-axis stroke-1 [stroke-dasharray:3_3]"
                  x1={x(cursor)}
                  x2={x(cursor)}
                  y1={PAD}
                  y2={base}
                />
                <circle
                  className="fill-chart-line stroke-surface stroke-[3]"
                  cx={x(cursor)}
                  cy={y(active.consultations)}
                  r={5}
                />
              </>
            )}
          </svg>

          {cursor !== null && active && (
            <div
              className="pointer-events-none absolute top-0 z-sticky rounded-md bg-fg px-3 py-2 text-xs leading-snug whitespace-nowrap text-surface shadow-md"
              style={{
                left: `${((cursor + 0.5) / count) * 100}%`,
                transform: tooltipShift(cursor, count),
              }}
            >
              <span className="font-semibold tabular-nums">
                {active.consultations}{' '}
                {active.consultations === 1 ? 'consultation' : 'consultations'}
              </span>
              <br />
              <span className="opacity-75">
                {grouped ? `Week of ${formatPointDate(active.day)}` : formatPointDate(active.day)}
              </span>
            </div>
          )}
        </div>

        <div className="relative col-start-2 grid h-5" aria-hidden="true">
          {xTicks.map((index, position) => (
            <span
              key={plotted[index]?.day ?? index}
              className={cx(
                'absolute top-0 -translate-x-1/2 text-xs leading-none whitespace-nowrap text-fg-subtle',
                position === 0 && 'left-0 translate-x-0',
                position === xTicks.length - 1 && 'right-0 left-auto translate-x-0',
              )}
              style={
                position === 0 || position === xTicks.length - 1
                  ? undefined
                  : { left: `${((index + 0.5) / count) * 100}%` }
              }
            >
              {formatAxisDate(plotted[index]?.day ?? '')}
            </span>
          ))}
        </div>
      </div>

      <div aria-live="polite" className="sr-only">
        {active
          ? `${grouped ? 'Week of ' : ''}${formatPointDate(active.day)}: ${active.consultations} ${unit}`
          : ''}
      </div>

      {/* Colour and position are not the only way to read this. A real button
          rather than <details>, whose marker triangle is drawn by the browser
          and cannot be themed. */}
      <div>
        <button
          type="button"
          aria-expanded={tableOpen}
          onClick={() => setTableOpen((open) => !open)}
          className="flex min-h-touch items-center gap-1 text-sm text-fg-muted hover:text-fg"
        >
          <Icon
            name="chevronRight"
            size="1rem"
            className={cx('transition-transform duration-150 ease-standard', tableOpen && 'rotate-90')}
          />
          View as table
        </button>
        {tableOpen && (
          <table className="mt-3 border-collapse text-sm [&_td]:border-b [&_td]:border-line-soft [&_td]:py-1 [&_td]:pr-4 [&_td:last-child]:tabular-nums [&_th]:border-b [&_th]:border-line-soft [&_th]:py-1 [&_th]:pr-4 [&_th]:text-left">
            <thead>
              <tr>
                <th scope="col">{grouped ? 'Week beginning' : 'Day'}</th>
                <th scope="col">Consultations</th>
              </tr>
            </thead>
            <tbody>
              {plotted.map((point) => (
                <tr key={point.day}>
                  <td>{formatPointDate(point.day)}</td>
                  <td>{point.consultations}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </figure>
  )
}

/** Weekly totals once a daily series stops being legible. Bucketed backwards
 *  from today, so only the oldest bucket can be short. */
function condense(points: TrendPoint[]): { points: TrendPoint[]; grouped: boolean } {
  if (points.length <= CONDENSE_ABOVE) return { points, grouped: false }
  const weeks: TrendPoint[] = []
  for (let end = points.length; end > 0; end -= 7) {
    const week = points.slice(Math.max(0, end - 7), end)
    weeks.unshift({
      day: week[0]?.day ?? '',
      consultations: week.reduce((sum, p) => sum + p.consultations, 0),
    })
  }
  return { points: weeks, grouped: true }
}

/** Near an edge the tooltip anchors to that edge instead of overhanging the card. */
function tooltipShift(index: number, count: number): string {
  const position = (index + 0.5) / count
  if (position < 0.15) return 'translateX(0)'
  if (position > 0.85) return 'translateX(-100%)'
  return 'translateX(-50%)'
}

/** Integer axis ticks, at most five: an axis reading 0, 0.25, 0.5 for a busiest
 *  day of one is nonsense. */
function niceScale(dataMax: number): { top: number; ticks: number[] } {
  const max = Math.max(1, Math.ceil(dataMax))
  for (const step of steps()) {
    const intervals = Math.ceil(max / step)
    if (intervals <= 4) {
      const top = step * intervals
      return {
        top,
        ticks: Array.from({ length: intervals + 1 }, (_, i) => i * step),
      }
    }
  }
  /* c8 ignore next */
  return { top: max, ticks: [0, max] }
}

function* steps(): Generator<number> {
  for (let magnitude = 1; magnitude <= 1e9; magnitude *= 10) {
    for (const base of [1, 2, 2.5, 5]) {
      const step = base * magnitude
      if (Number.isInteger(step)) yield step
    }
  }
}
